// src/store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/auth';

// ─── Normalise user from any API response ─────────────────────────────────────
const normaliseUser = (user) => {
  if (!user) return null;

  const isPremium =
    user.is_premium === true ||
    user.has_premium_access === true ||
    (user.premium_expiry_date &&
      new Date(user.premium_expiry_date) > new Date());

  const subscriptionInfo = user.subscription_info || null;

  return {
    ...user,
    is_premium: isPremium,
    has_premium_access: isPremium,
    subscription_info: subscriptionInfo,
  };
};

const initialState = {
  user: normaliseUser(authService.getCurrentUser()),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      // Backend returns { user, access, refresh }
      return {
        user: normaliseUser(response.user),
        access: response.access,
        refresh: response.refresh
      };
    } catch (error) {
      const message = error.response?.data?.detail || 
                      error.response?.data?.non_field_errors?.[0] ||
                      'Login failed. Please check your credentials.';
      return rejectWithValue(message);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      // Supporting seamless login: backend should return user + tokens
      return {
        user: normaliseUser(response.user),
        access: response.access,
        refresh: response.refresh
      };
    } catch (error) {
      if (error.response && error.response.data) {
        const data = error.response.data;
        const firstError = Object.values(data)[0];
        return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError);
      }
      return rejectWithValue('Registration failed. Please try again.');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getProfile();
      return normaliseUser(user);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail || 'Failed to fetch profile'
      );
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setLoading: (state, action) => { state.isLoading = action.payload; },
    setPremium: (state, action) => {
      if (state.user) {
        state.user.is_premium = action.payload;
        state.user.has_premium_access = action.payload;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (s) => { 
        s.isLoading = true; 
        s.error = null; 
      })
      .addCase(login.fulfilled, (s, a) => {
        s.isLoading = false;
        s.isAuthenticated = true;
        s.user = a.payload.user;
        localStorage.setItem('user', JSON.stringify(s.user));
      })
      .addCase(login.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
        s.isAuthenticated = false;
      })

      // Register (Consolidated to fix the duplicate case error)
      .addCase(register.pending, (s) => { 
        s.isLoading = true; 
        s.error = null; 
      })
      .addCase(register.fulfilled, (s, a) => {
        s.isLoading = false;
        s.isAuthenticated = true; // Seamless jump to dashboard
        s.user = a.payload.user || a.payload; 
        localStorage.setItem('user', JSON.stringify(s.user));
      })
      .addCase(register.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      })

      // Logout
      .addCase(logout.fulfilled, (s) => {
        s.isLoading = false;
        s.user = null;
        s.isAuthenticated = false;
        localStorage.removeItem('user');
      })

      // Fetch Profile
      .addCase(fetchProfile.fulfilled, (s, a) => {
        s.isLoading = false;
        s.isAuthenticated = true;
        s.user = a.payload;
        localStorage.setItem('user', JSON.stringify(a.payload));
      })
      .addCase(fetchProfile.rejected, (s) => {
        s.isLoading = false;
        s.isAuthenticated = false;
        s.user = null;
        localStorage.removeItem('user');
      });
  },
});

export const { clearError, setLoading, setPremium } = authSlice.actions;
export default authSlice.reducer;